import React, { useRef, useEffect } from 'react';
import '../../styles/pages/device-list.css';
import {
  DeviceDashboardDto,
  ParameterDtoPreferred,
} from '@/services/deviceApi';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import WebMap from '@arcgis/core/WebMap';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import HelperFunctions, { options, getDeviceStatusLabel } from '../../utils/helperFunctions';
import Locate from '@arcgis/core/widgets/Locate';
import { getUserTimezone } from '../../utils/timezoneUtils';

import { useNavigate } from 'react-router-dom';
// import Basemap from "@arcgis/core/Basemap";

interface Column {
  driverID: number;
  parameterName: string;
  unit: string | null;
}

interface Parameter {
  value: number | string | null;
  subIndex: number | null;
}

const Devicemapview: React.FC<{
  devices: DeviceDashboardDto[];
  parameters: ParameterDtoPreferred[];
}> = ({ devices, parameters }) => {
  const theme = document.documentElement.getAttribute("data-theme") ?? "light";
  const coloums = parameters;
  const navigate = useNavigate();
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<MapView | null>(null);
  const deviceLayer = useRef<GraphicsLayer | null>(null);
  const userPreferences = localStorage.getItem('userPreferences');
  const timeZone = userPreferences ? JSON.parse(userPreferences).timezone : getUserTimezone();

    const webmap = new WebMap({
      basemap: theme === 'light' ? 'streets-navigation-vector' : 'dark-gray-vector',
    });

  // Function to calculate bounds of all device locations
  const calculateDeviceBounds = (devices: DeviceDashboardDto[]) => {
    if (!devices || devices.length === 0) {
      return { center: [0, 0], zoom: 2 };
    }

    const validDevices = devices.filter(
      device =>
        device.latitude &&
        device.longitude &&
        !isNaN(Number(device.latitude)) &&
        !isNaN(Number(device.longitude))
    );

    if (validDevices.length === 0) {
      return { center: [0, 0], zoom: 2 };
    }

    const latitudes = validDevices.map(device => Number(device.latitude));
    const longitudes = validDevices.map(device => Number(device.longitude));

    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Add more padding around the bounds (25% on each side for better visibility)
    const latPadding = Math.max((maxLat - minLat) * 0.25, 0.01); // Minimum 0.01 degree padding
    const lngPadding = Math.max((maxLng - minLng) * 0.25, 0.01); // Minimum 0.01 degree padding

    const paddedMinLat = minLat - latPadding;
    const paddedMaxLat = maxLat + latPadding;
    const paddedMinLng = minLng - lngPadding;
    const paddedMaxLng = maxLng + lngPadding;

    // Calculate appropriate zoom level based on the spread of devices
    const latDiff = paddedMaxLat - paddedMinLat;
    const lngDiff = paddedMaxLng - paddedMinLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    // Determine zoom level based on the spread (more conservative zoom levels)
    let zoom = 2;
    if (maxDiff > 20) zoom = 3;
    else if (maxDiff > 10) zoom = 4;
    else if (maxDiff > 5) zoom = 5;
    else if (maxDiff > 2) zoom = 6;
    else if (maxDiff > 1) zoom = 7;
    else if (maxDiff > 0.5) zoom = 8;
    else if (maxDiff > 0.2) zoom = 9;
    else if (maxDiff > 0.1) zoom = 10;
    else zoom = 11;

    return {
      center: [centerLng, centerLat],
      zoom: zoom,
      extent: {
        xmin: paddedMinLng,
        ymin: paddedMinLat,
        xmax: paddedMaxLng,
        ymax: paddedMaxLat,
      },
    };
  };

  useEffect(() => {
    if (!mapDiv.current || viewRef.current) return;

    // Test if the global function is available
    //  console.log('Global function available:', typeof (window as any).toggleDeviceParameters);

    // Calculate bounds of all device locations
    const deviceBounds = calculateDeviceBounds(devices);

    const view = new MapView({
      container: mapDiv.current,
      map: webmap,
      constraints: { minZoom: 2 },
      center: deviceBounds.center,
      zoom: deviceBounds.zoom,
      popup: {
        dockEnabled: false,
        dockOptions: {
          buttonEnabled: false,
          position: 'bottom-center',
        },
        actions: [],
        defaultPopupTemplateEnabled: false,
      },
    });

    view.ui.move('zoom', 'top-right');

    const locateContainer = document.createElement('div');

    // Enable user location button
    const locateBtn = new Locate({
      view: view,
      graphic: null as any,
      container: locateContainer,
    });

    locateBtn.on('locate', event => {
      console.log('Locate widget found location:', event);

      if (event.position) {
        console.log('Locate widget found location:', { 
          lat: event.position.coords.latitude, 
          lng: event.position.coords.longitude 
        });
        // handleLocationSelect(event.position.coords.latitude, event.position.coords.longitude);
        view.graphics.removeAll();
      }
    });

    // Handle locate widget errors silently
    locateBtn.on('locate-error', event => {
      console.log(
        'Locate widget error:',
        event.error?.message || 'Location access denied'
      );
      // Silently handle the error - no popup
    });

    // view.ui.add(locateBtn, 'top-right');

    const widgetWrapper = document.createElement('div');
    widgetWrapper.style.display = 'flex';
    widgetWrapper.style.alignItems = 'center';
    widgetWrapper.style.gap = '0.75rem';
    widgetWrapper.appendChild(locateContainer);

    view.ui.add(widgetWrapper, {
      position: 'top-right',
      index: 1, // ensures it appears below zoom controls
    });

    deviceLayer.current = new GraphicsLayer();
    webmap.add(deviceLayer.current);
    viewRef.current = view;
    view.popupEnabled = false;

    // Wait for the view to be ready before setting the view
    view
      .when(() => {
        // Set the center and zoom directly - more reliable than goTo
        view.center = deviceBounds.center;
        view.zoom = deviceBounds.zoom;
      })
      .catch(error => {
        console.warn('Error initializing map view:', error);
        // Fallback to default view if there's an error
        view.center = [0, 0];
        view.zoom = 2;
      });

    if (view && view.popup) {
      view.popup.visibleElements = {
        heading: false,
        closeButton: false,
        actionBar: false,
        collapseButton: false,
        title: false,
      } as any;
    }

    // Add click event to open popup
    // view.on("click", (event) => {
    //   view.hitTest(event).then((response) => {
    //     if (response.results.length > 0) {
    //       const result = response.results[0] as any;
    //       if (result.graphic && result.graphic.attributes && result.graphic.attributes.deviceId) {
    //         if (view.popup) {
    //           view.popup.open({
    //             features: [result.graphic],
    //             location: event.mapPoint
    //           });
    //         }
    //       }
    //     }
    //   });
    // });

    view.on('click', event => {
      // Hide tooltip immediately when clicking
      const tooltip = document.getElementById('device-tooltip');
      if (tooltip) {
        tooltip.style.display = 'none';
      }

      view.hitTest(event).then(response => {
        if (response.results.length > 0) {
          const results = response.results as any;
          const result = results.find(
            (r: {
              graphic: {
                symbol: any;
                attributes: { deviceId: any };
              };
            }) =>
              r.graphic?.attributes?.deviceId &&
              r.graphic?.symbol?.type === 'simple-marker'
          );
          const zoomLevel = view.zoom;
          if (result?.graphic?.attributes?.deviceId) {
            if (zoomLevel > 15) {
              // Reset cursor to default when opening popup
              if (view.container) {
                view.container.style.cursor = 'default';
              }

              if (view.popup) {
                view.openPopup({
                  features: [result.graphic],
                  location: event.mapPoint,
                });
              }

              // Only adjust position if popup is not fully visible
              setTimeout(() => {
                if (view.popup && view.popup.visible) {
                  const popupElement = view.popup.container;
                  if (popupElement) {
                    const popupRect = popupElement.getBoundingClientRect();
                    const mapRect = view.container?.getBoundingClientRect();

                    if (mapRect) {
                      // Check if popup is clipped or not fully visible
                      const isClippedRight =
                        popupRect.right > mapRect.right - 10;
                      const isClippedBottom =
                        popupRect.bottom > mapRect.bottom - 10;
                      const isClippedLeft = popupRect.left < mapRect.left + 10;
                      const isClippedTop = popupRect.top < mapRect.top - 20;

                      // Only move if popup is actually clipped
                      if ((isClippedRight || isClippedBottom || isClippedLeft || isClippedTop) && window.innerWidth > 600) {
                        // Calculate screen position for the device (bottom center of the map)
                        const screenX = mapRect.width / 2; // Center horizontally
                        const screenY = mapRect.height - 100; // Near bottom, leaving space for popup

                        // Convert screen coordinates to map coordinates
                        const mapPoint = view.toMap({ x: screenX, y: screenY });

                        // Move the map so the clicked device appears at the calculated screen position
                        view.goTo({
                          center: [mapPoint.longitude, mapPoint.latitude],
                          duration: 1200,
                        });
                      }
                    }
                  }
                }
              }, 150);
            } else {
              view.goTo(
                {
                  center: [
                    result.graphic.geometry.longitude,
                    result.graphic.geometry.latitude,
                  ],
                  zoom: 16,
                },
                { duration: 3000 }
              );
            }
          } else {
            try {
              view?.popup?.close();
            } catch (error) {
              console.log(error);
            }
          }
        }
      });
    });

    // Add hover event to show tooltip
    view.on('pointer-move', event => {
      // Check if popup is currently visible - if so, don't show tooltip
      if (view.popup && view.popup.visible) {
        const tooltip = document.getElementById('device-tooltip');
        if (tooltip) {
          tooltip.style.display = 'none';
        }
        return;
      }

      view.hitTest(event).then(response => {
        if (response.results.length > 0) {
          const result = response.results[0] as any;
          if (
            result.graphic &&
            result.graphic.attributes &&
            result.graphic.attributes.deviceId
          ) {
            const deviceName = result.graphic.attributes.deviceName || 'Device';
            if (view.container) {
              view.container.style.cursor = 'pointer';
            }

            // Create or update tooltip
            let tooltip = document.getElementById('device-tooltip');
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.id = 'device-tooltip';
              tooltip.style.cssText = `
                  position: absolute;
                  background: var(--modal-background);
                  color: var(--color-text);
                  padding: 6px 10px;
                  border-radius: 6px;
                  font-size: 13px;
                  font-weight: 500;
                  pointer-events: none;
                  z-index: 1000;
                  transform: translate(-50%, -100%);
                  margin-top: -8px;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                  white-space: nowrap;
                  max-width: 200px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `;
              // Append to map container
              if (view.container) {
                view.container.appendChild(tooltip);
              }
            }

            tooltip.textContent = deviceName;

            // Position tooltip relative to the map container
            if (view.container) {
              const mapRect = view.container.getBoundingClientRect();
              const tooltipWidth = 200;
              const tooltipHeight = 30;

              // Convert screen coordinates to map container coordinates
              const left = event.x;
              const top = event.y - tooltipHeight + 8;

              // Position tooltip with proper bounds checking
              let finalLeft = left;
              let finalTop = top;

              // Horizontal bounds checking
              if (finalLeft - tooltipWidth / 2 < 0) {
                finalLeft = tooltipWidth / 2;
              } else if (finalLeft + tooltipWidth / 2 > mapRect.width) {
                finalLeft = mapRect.width - tooltipWidth / 2;
              }

              // Vertical bounds checking
              if (finalTop < 0) {
                finalTop = event.y + 20; // Show below cursor
              }

              tooltip.style.left = finalLeft + 'px';
              tooltip.style.top = finalTop + 'px';
              tooltip.style.display = 'block';
            }
          } else {
            if (view.container) {
              view.container.style.cursor = 'default';
            }
            const tooltip = document.getElementById('device-tooltip');
            if (tooltip) {
              tooltip.style.display = 'none';
            }
          }
        } else {
          if (view.container) {
            view.container.style.cursor = 'default';
          }
          const tooltip = document.getElementById('device-tooltip');
          if (tooltip) {
            tooltip.style.display = 'none';
          }
        }
      });
    });

    // function shortenDeviceName(name: any): string {
    //   const newName = name.split(' ');
    //   if (newName.length <= 1) {
    //     return newName.join('').substring(0, 4) + '..';
    //   }
    //   return newName
    //     .map((word: string) => word.charAt(0).toUpperCase())
    //     .join('');
    // }

    function generateColumnsHTML(
      columns: Column[],
      parameters: Record<number, Parameter>
    ): string {
      if (!columns || !parameters) return '';

      // Find the driverID with max subIndex
      let primaryDriverID: number | null = null;
      let maxSubIndex = -Infinity;

      for (const [driverID, param] of Object.entries(parameters)) {
        if (param && param.subIndex !== null && param.subIndex > maxSubIndex) {
          maxSubIndex = param.subIndex;
          primaryDriverID = Number(driverID);
        }
      }

      // Sort columns to put primary parameter first
      const sortedColumns = [...columns].sort((a, b) => {
        if (a.parameterName === 'AQI') return -1;
        if (b.parameterName === 'AQI') return 1;
        if (a.driverID === primaryDriverID) return -1; // Primary parameter first
        if (b.driverID === primaryDriverID) return 1;
        return 0; // Keep original order for non-primary parameters
      });

      // Filter out parameters with null values, AQI, and Primary Pollutant
      const validColumns = sortedColumns.filter((col: Column) => {
        const param = parameters[col.driverID];
        const isValid = param && param.value != null && !isNaN(Number(param.value));
        const isAQI = col.parameterName === 'AQI';
        const isPrimary = col.driverID === primaryDriverID;
        return isValid && !isAQI && !isPrimary;
      });

      const html = validColumns
        .map((col: Column) => {
          const param = parameters[col.driverID];
          let value;
          if (
            col.parameterName === 'Temperature' ||
            col.parameterName === 'Humidity'
          ) {
            value = Math.round(Number(param.value));
          } else {
            value = Math.round(Number(param.value) * 100) / 100;
          }
          const isPrimary = col.driverID === primaryDriverID;
          const unit = col.unit ? ` (${col.unit.trim()})` : '';

          return `
            <div class="flex justify-between items-center py-1 px-2 border-b border-gray-100 last:border-b-0">
              <div class="font-light text-[var(--color-text)]" style="font-size: 10px;">
                ${col.parameterName}${isPrimary ? ' (Primary)' : ''}${unit}
              </div>
              <div style="font-size: 10px; font-weight: 600; color: var(--color-text);">
                  ${value}
              </div>
            </div>
          `;
        })
        .join('');

      return `
        <div class="p-1 bg-transparent"">
          ${html}
        </div>
      `;
    }

    devices.forEach(
      ({
        latitude,
        longitude,
        deviceId,
        deviceName,
        deviceModel,
        isActive,
        operationalStatus,
        lastUpdated,
        parameters,
      }) => {
        //   const aqiValue = 20
        let aqiColor = '#A0ED9F';

        const point = new Point({
          latitude: Number(latitude),
          longitude: Number(longitude),
        });

        const aqiParam = Object.values(parameters).find(
          (p: any) => p?.parameterName?.toLowerCase() === 'aqi'
        );

        aqiColor = aqiParam?.value
          ? HelperFunctions.GetParameterColor(aqiParam?.value, 'AQI')
          : '#d6d4d0';
        // Base circle graphic
        const circleGraphic = new Graphic({
          geometry: point,
          symbol: new SimpleMarkerSymbol({
            color: aqiColor,
            style: 'circle',
            size: 30,
            outline: {
              color: '#FFFFFF',
              width: 1,
            },
          }),
          attributes: {
            deviceId,
            deviceName,
            lastMeasured: lastUpdated
              ? lastUpdated.replace('T', ' ').replace('Z', '')
              : null,
            aqi:
              aqiParam?.value != null
                ? Math.round(Number(aqiParam.value) * 100) / 100
                : 'N/A',
            aqiColor: aqiColor,
            status: getDeviceStatusLabel(isActive, operationalStatus).label,
            model: deviceModel,
            parameters,
            // so2: 10,
            // pm25: 23,
            // pm10: 6,
            // no2: 38,
            // o3: 20
            // co: 10,
          },
          popupTemplate: {
            title: '{deviceName}',
            content: (feature: any) => {
              const attr = feature.graphic.attributes;
              const mainPollutantData =
                HelperFunctions.getMainPollutantAndValue(
                  attr.parameters || {},
                  coloums
                );
              const mainPollutant = mainPollutantData.pollutant;
              const mainPollutantValue = mainPollutantData.value;
              const mainPollutantUnit = mainPollutantData.unit;
              const lastMeasured = attr.lastMeasured
                ? new Date(attr.lastMeasured).toLocaleString('en-US', {timeZone: timeZone, ...options})
                : 'N/A';

              // Create container
              const container = document.createElement('div');
              container.style.width = '100%';
              container.style.backgroundColor = 'var(--modal-background)';
              ((container.style.fontFamily = 'DM Sans'), 'Inter');

              // Header section
              const headerDiv = document.createElement('div');
              headerDiv.style.cssText =
                'display: flex; align-items: flex-start; gap: 12px; padding: 12px;';

              // AQI container with box and label below
              const aqiContainer = document.createElement('div');
              aqiContainer.style.cssText = 'display: flex; flex-direction: column; align-items: center; gap: 4px;';

              // AQI box
              const aqiCircle = document.createElement('div');
              aqiCircle.style.cssText = `background-color: ${attr.aqiColor || '#A0ED9F'}; padding: 10px; border-radius: 8px; color: #031716; display: flex; align-items: center; justify-content: center; font-weight: bold; min-width: 30px; height: 30px; box-sizing: border-box;`;

              const aqiValue = document.createElement('div');
              aqiValue.textContent = attr.aqi;

              aqiCircle.appendChild(aqiValue);

              // AQI label below the box
              const aqiLabel = document.createElement('div');
              aqiLabel.style.cssText = 'font-size: 0.75rem; font-weight: 700; color: var(--color-sub-title-text); text-align: center; margin-top: -2px;';
              aqiLabel.textContent = 'AQI';

              aqiContainer.appendChild(aqiCircle);
              aqiContainer.appendChild(aqiLabel);

              const deviceInfo = document.createElement('div');
              deviceInfo.style.flex = '1';
              deviceInfo.style.display = 'flex';
              deviceInfo.style.flexDirection = 'column';
              deviceInfo.style.gap = '4px';

              const deviceNameRow = document.createElement('div');
              deviceNameRow.style.cssText =
                'display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%;';

              const deviceName = document.createElement('div');
              deviceName.style.cssText =
                'font-size: 12px; font-weight: 600; color: #005EE5; text-decoration: underline; cursor: pointer; flex-shrink: 0;';
              deviceName.textContent = attr.deviceName;
              deviceName.addEventListener('click', () =>
                navigate(`/devices/${attr.deviceId}`)
              );
              const statusBadge = document.createElement('div');
              if (attr.status === 'Active') {
                statusBadge.style.cssText =
                  'padding: 2px 10px; border-radius: 16px; font-size: 10px; font-weight: 500; background: #47C08F; color: white; flex-shrink: 0; white-space: nowrap;';
              } else {
                statusBadge.style.cssText =
                  'padding: 2px 10px; border-radius: 16px; font-size: 10px; font-weight: 500; background: #E67E7D;; color: white; flex-shrink: 0; white-space: nowrap;';
              }
              statusBadge.textContent = attr.status;

              deviceNameRow.appendChild(deviceName);
              deviceNameRow.appendChild(statusBadge);

              // Container for Model and Updated on in the same line
              const modelAndUpdatedRow = document.createElement('div');
              modelAndUpdatedRow.style.cssText =
                'display: flex; flex-direction: column; font-size: 10px; line-height: 1.3;';

              const modelText = document.createElement('div');
              modelText.style.cssText =
                'font-weight: 400; color: var(--color-text);';
              modelText.textContent = `Model: ${attr.model}`;

              const lastMeasuredText = document.createElement('div');
              lastMeasuredText.style.cssText =
                'font-weight: 300; color: var(--color-sub-title-text);';
              lastMeasuredText.innerHTML = `Updated on: <span style="font-weight: 500;">${lastMeasured}</span>`;

              modelAndUpdatedRow.appendChild(modelText);
              modelAndUpdatedRow.appendChild(lastMeasuredText);

              deviceInfo.appendChild(deviceNameRow);
              deviceInfo.appendChild(modelAndUpdatedRow);

              headerDiv.appendChild(aqiContainer);
              headerDiv.appendChild(deviceInfo);
              container.appendChild(headerDiv);

              // Divider
              const divider = document.createElement('hr');
              divider.style.cssText =
                'margin: 0; border: none; border-top: 1px solid #eee;';
              container.appendChild(divider);

              // Main Pollutant Section - Compact one line
              const mainPollutantDiv = document.createElement('div');
              mainPollutantDiv.style.cssText =
                'padding: 4px 10px; display: flex; justify-content: space-between; align-items: center;';

              const mainPollutantInfo = document.createElement('div');
              mainPollutantInfo.style.cssText =
                'display: flex; align-items: center; gap: 6px;';

              const mainPollutantLabel = document.createElement('span');
              mainPollutantLabel.style.cssText =
                'font-size: 10px; font-weight: 300; color: var(--color-text);';
              mainPollutantLabel.textContent = 'Main Pollutant:';

              const mainPollutantName = document.createElement('span');
              mainPollutantName.style.cssText =
                'font-size: 10px; font-weight: 300; color: var(--color-text);';
              mainPollutantName.textContent = mainPollutant;

              // Container for value and unit to keep them close together
              const valueUnitContainer = document.createElement('span');
              valueUnitContainer.style.cssText =
                'display: flex; align-items: center; gap: 2px;';

              const mainPollutantValueSpan = document.createElement('span');
              mainPollutantValueSpan.style.cssText =
                'font-size: 10px; font-weight: 600; color: var(--color-text);';
              mainPollutantValueSpan.textContent = mainPollutantValue;

              const mainPollutantUnitSpan = document.createElement('span');
              mainPollutantUnitSpan.style.cssText =
                'font-size: 9px; font-weight: 400; color: var(--color-sub-title-text);';
              mainPollutantUnitSpan.textContent = mainPollutantUnit;

              valueUnitContainer.appendChild(mainPollutantValueSpan);
              valueUnitContainer.appendChild(mainPollutantUnitSpan);

              mainPollutantInfo.appendChild(mainPollutantLabel);
              mainPollutantInfo.appendChild(mainPollutantName);
              mainPollutantInfo.appendChild(valueUnitContainer);

              const viewMoreBtn = document.createElement('button');
              viewMoreBtn.id = `view-more-btn-${attr.deviceId}`;
              viewMoreBtn.setAttribute('data-device-id', attr.deviceId);
              viewMoreBtn.style.cssText =
                'color: #2563eb; text-decoration: underline; background: none; border: none; cursor: pointer; font-size: 11px; font-weight: 500;';
              viewMoreBtn.textContent = 'View More';

              // Add click event listener directly to the button
              viewMoreBtn.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Button clicked for device:', attr.deviceId);
                const allParamsDiv = document.getElementById(
                  `all-parameters-${attr.deviceId}`
                );
                if (allParamsDiv) {
                  if (allParamsDiv.style.display === 'none') {
                    allParamsDiv.style.display = 'block';
                    viewMoreBtn.textContent = 'View Less';
                    console.log('Showing all parameters');
                  } else {
                    allParamsDiv.style.display = 'none';
                    viewMoreBtn.textContent = 'View More';
                    console.log('Hiding all parameters');
                  }
                } else {
                  console.log('All params div not found');
                }
              });

              mainPollutantDiv.appendChild(mainPollutantInfo);
              mainPollutantDiv.appendChild(viewMoreBtn);
              container.appendChild(mainPollutantDiv);

              // All Parameters Section (Initially Hidden)
              const allParamsDiv = document.createElement('div');
              allParamsDiv.id = `all-parameters-${attr.deviceId}`;
              allParamsDiv.style.display = 'none';

              const allParamsDivider = document.createElement('hr');
              allParamsDivider.style.cssText =
                'margin: 0; border: none; border-top: 1px solid #eee;';
              allParamsDiv.appendChild(allParamsDivider);

              const allParamsContent = document.createElement('div');
              allParamsContent.style.cssText =
                'padding: 4px 10px;';

              const allParamsTitle = document.createElement('h3');
              allParamsTitle.style.cssText =
                'font-size: 10px; font-weight: 600; color: var(--color-text); margin: 0 0 4px 0;';
              allParamsTitle.textContent = 'All Parameters';

              allParamsContent.appendChild(allParamsTitle);

              // Add the existing parameters HTML
              const paramsContainer = document.createElement('div');
              paramsContainer.innerHTML = generateColumnsHTML(
                coloums,
                attr.parameters
              );
              allParamsContent.appendChild(paramsContainer);

              allParamsDiv.appendChild(allParamsContent);
              container.appendChild(allParamsDiv);

              return container;
            },
          },
        });

        // AQI text overlay
        const textGraphic = new Graphic({
          geometry: point,
          symbol: new TextSymbol({
            // text: shortenDeviceName(deviceName),
            text: aqiParam?.value ? aqiParam.value.toString() : 'N/A',
            color: '#4C5C68',
            haloColor: '#ffffff',
            haloSize: 1,
            font: {
              size: 8,
              weight: 'bold',
            },
            yoffset: -2,
          }),
          attributes: {
            deviceId,
            deviceName,
          },
        });

        // Add both graphics
        deviceLayer?.current?.addMany([circleGraphic, textGraphic]);
      }
    );

    // Cleanup function
    return () => {
      const tooltip = document.getElementById('device-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    };
  }, []);

  return <div className="dashboard-map-view relative" ref={mapDiv}></div>;
};

export default Devicemapview;
